import classNames from "classnames";
import { Anchor, AnchorProps } from "../Anchor/Anchor";
import {
	FontIconName,
	Icon,
	IconValue
} from "../Icon/Icon";
import {
	PropsWithChildren,
	ReactElement,
	useMemo
} from "react";
import styles from "./DetailsBox.module.less";
import { IconButton } from "../IconButton/IconButton";
import { CalendarStylesColor } from "client/Misc/commonTypes";
import {
	MediaQueryKind,
	useCallbackWithoutArguments,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ResponsiveMenu } from "../ResponsiveMenu/ResponsiveMenu";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import { Toolbar } from "../Toolbar/Toolbar";
import { BusyIndicator } from "../BusyIndicator/BusyIndicator";
import { ButtonKind } from "../Button/Button";
import { ActionListItem } from "../ActionList/ActionListItem";
import { partition } from "client_legacy/ndaw/arrayHelpers";

export type DetailsBoxVariant =
	| "success"
	| "info"
	| "danger"
	| "warning"
	| "secondary"
	| "default"
	| "no-color"
	| CalendarStylesColor;

export type DetailsBoxActionGroup = {
	name: string;
	text: string;
	icon?: FontIconName;
	kind: ButtonKind;
};

export type DetailsBoxAction = {
	kind: ButtonKind;
	trackingId: string;
	text: string;
	icon?: FontIconName;
	onClick: () => void;
	group?: DetailsBoxActionGroup;
};

export type DetailsBoxProps = {
	variant?: DetailsBoxVariant;
	title: string;
	onClose(): void;
	onEnlarge?: () => void;
	responsiveMode?: "auto" | "never";
	hasBusyIndicator?: boolean;
	trackingId?: string;
	width?: "auto";
	actions?: readonly DetailsBoxAction[];
};

export const DetailsBox = (props: PropsWithChildren<DetailsBoxProps>): ReactElement => {
	const {
		variant = "default",
		title,
		onClose: onCloseFromProps,
		onEnlarge,
		children,
		responsiveMode = "auto",
		hasBusyIndicator = false,
		trackingId,
		width,
		actions
	} = props;

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const onClose: () => void = useCallbackWithoutArguments(onCloseFromProps);

	const renderedActions = useMemo(
		(): ReactElement | null => {
			return actions ? detailsBoxActionsFactory(actions) : null;
		},
		[actions]
	);

	if (responsiveMode !== "never" && isResponsive) {
		return (
			<ResponsiveMenu
				onClose={onClose}
				trackingId={trackingId}
				hasBusyIndicator={hasBusyIndicator}
			>
				<ResponsiveMenu.Header
					title={title}
					actions={
						onEnlarge
							? [{
								icon: "arrow-up-right-and-arrow-down-left-from-center",
								onClick: onEnlarge,
								trackingId: "enlarge",
								kind: "transparent-colored"
							}]
							: undefined
					}
				/>

				<ResponsiveMenu.Body>
					{children}
				</ResponsiveMenu.Body>

				{isNotNil(renderedActions) && (
					<ResponsiveMenu.Footer>
						<Toolbar horizontalAlignment="right">
							{renderedActions}
						</Toolbar>
					</ResponsiveMenu.Footer>
				)}
			</ResponsiveMenu>
		);
	}

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				variantToClassNameMapping[variant],
				{
					[styles.root_widthAuto]: width === "auto"
				}
			)}
		>
			<div className={styles.header}>
				<h3
					{...setTrackingId("title")}
					className={styles.title}>{title}
				</h3>
				<div className={styles.headerButton}>
					{onEnlarge && (
						<IconButton
							kind="transparent-colored"
							onClick={onEnlarge}
							trackingId="enlarge"
							icon="arrow-up-right-and-arrow-down-left-from-center"
						/>
					)}
					<IconButton
						kind="transparent"
						onClick={onClose}
						trackingId="close"
						icon="xmark"
						shape="round"
					/>
				</div>
			</div>
			<div>
				{children}
			</div>
			{isNotNil(renderedActions) && (
				<div
					className={styles.footer}
				>
					<Toolbar horizontalAlignment="right">
						{renderedActions}
					</Toolbar>
				</div>
			)}
			{hasBusyIndicator && (
				<BusyIndicator />
			)}
		</div>
	);
};
DetailsBox.displayName = "DetailsBox";

const variantToClassNameMapping: { [k in DetailsBoxVariant]: string | undefined; } = {
	default: undefined,
	success: styles.root_success,
	warning: styles.root_warning,
	danger: styles.root_danger,
	secondary: styles.root_secondary,
	info: styles.root_info,
	"no-color": styles.root_noColor,
	"calendar-1": styles.root_calendar1,
	"calendar-2": styles.root_calendar2,
	"calendar-3": styles.root_calendar3,
	"calendar-4": styles.root_calendar4,
	"calendar-5": styles.root_calendar5,
	"calendar-6": styles.root_calendar6,
	"calendar-7": styles.root_calendar7,
	"calendar-8": styles.root_calendar8,
	"calendar-9": styles.root_calendar9,
	"calendar-10": styles.root_calendar10,
	"calendar-11": styles.root_calendar11,
	"calendar-12": styles.root_calendar12,
	"calendar-13": styles.root_calendar13,
	"calendar-14": styles.root_calendar14,
	"calendar-15": styles.root_calendar15,
	"calendar-16": styles.root_calendar16,
};

type ItemProps = {
	icon?: IconValue;
	variant?: "default" | "input-container";
};

const Item = (props: PropsWithChildren<ItemProps>): ReactElement => {
	const {
		icon,
		variant = "default",
		children
	} = props;

	return (
		<li className={classNames(
			styles.listItem,
			{
				[styles.listItem_inputContainer]: variant === "input-container"
			}
		)}>
			{isNotNil(icon) && (
				<span className={styles.listItemIconContainer}>
					<Icon value={icon} />
				</span>
			)}

			<span className={styles.listItemContent}>{children}</span>
		</li>
	);
};

Item.displayName = "Item";

const List = (props: PropsWithChildren<{}>): ReactElement => {
	const { children } = props;

	return (
		<ul className={styles.list}>
			{children}
		</ul>
	);
};

List.displayName = "DetailsBox.List";

const Divider = (): ReactElement => {

	return (
		<li className={styles.detailsListDivider}/>
	);
};

Divider.displayName = "Divider";

const DetailsBoxListAnchor = (props: AnchorProps): ReactElement => {
	const {
		children,
		...otherProps
	} = props;

	return (
		<li>
			<Anchor
				{...otherProps}
			>
				{children}
			</Anchor>
		</li>
	);
};

DetailsBoxListAnchor.displayName = "DetailsBoxList.Anchor";

const DetailsBoxListSubHeader = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<h4 className={styles.subHeader}>{children}</h4>
	);
};

DetailsBoxListSubHeader.displayName = "DetailsBoxList.SubHeader";
DetailsBox.SubHeader = DetailsBoxListSubHeader;
DetailsBox.Item = Item;
DetailsBox.List = List;
DetailsBox.Divider = Divider;
DetailsBox.Anchor = Anchor;

function detailsBoxActionsFactory(actions: readonly DetailsBoxAction[]): ReactElement {
	let actionsCopy: DetailsBoxAction[] = [...actions];
	const result: ReactElement[] = [];

	for (let i = 0; i < actionsCopy.length; i++) {
		const item: DetailsBoxAction = actionsCopy[i];
		const { group } = item;

		if (isNotNil(group)) {
			const [groupItems, rest] = partition(
				actionsCopy,
				(x) => x.group?.name === group.name
			);

			actionsCopy = rest;

			result.push(
				<Toolbar.MenuButton
					kind={group.kind}
					text={group.text}
					rightIcon="chevron-down"
					icon={group.icon}
					trackingId={group.name}
					key={group.name}
				>
					{groupItems.map((x: DetailsBoxAction): ReactElement => (
						<ActionListItem
							key={x.trackingId}
							text={x.text}
							onClick={x.onClick}
							trackingId={x.trackingId}
							icon={x.icon}
						/>
					))}
				</Toolbar.MenuButton>
			);
		} else {
			result.push(
				<Toolbar.Button
					key={item.trackingId}
					kind={item.kind}
					text={item.text}
					onClick={item.onClick}
					icon={item.icon}
					trackingId={item.trackingId}
				/>
			);
		}
	}

	return (
		<>
			{result}
		</>
	);
}